import React, { useMemo } from "react";
import { FaEye, FaHandshake } from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/partenairesPhotoServices";

import { PARTENAIRE_FALLBACK_IMAGE } from "../utils/partenairesSettingsUtils";

export default function PartenairesPreviewCard({ partenaires }) {
  const activePartenaires = useMemo(() => {
    return (Array.isArray(partenaires) ? partenaires : [])
      .filter((partenaire) => partenaire?.is_active)
      .slice()
      .sort((a, b) => Number(a?.ordre || 0) - Number(b?.ordre || 0));
  }, [partenaires]);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Prévisualisation
            </p>

            <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaHandshake className="text-indigo-600 dark:text-indigo-300" />
              Aperçu public des partenaires
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Aperçu simplifié des partenaires actifs, dans leur ordre
              d’affichage.
            </p>
          </div>

          <span className="inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            <FaEye />
            {activePartenaires.length} visible(s)
          </span>
        </div>

        {activePartenaires.length ? (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {activePartenaires.map((partenaire) => {
                const logoUrl =
                  toAbsolutePhotoUrl(partenaire.photo_url) ||
                  PARTENAIRE_FALLBACK_IMAGE;

                return (
                  <article
                    key={partenaire.id}
                    className="group overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex h-24 items-center justify-center rounded-2xl bg-slate-50 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <img
                        src={logoUrl}
                        alt={partenaire.nom || "Partenaire"}
                        className="max-h-20 w-full object-contain p-2"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        onError={(event) => {
                          event.currentTarget.src = PARTENAIRE_FALLBACK_IMAGE;
                        }}
                      />
                    </div>

                    <div className="mt-3">
                      <p className="line-clamp-1 font-bold text-slate-900 dark:text-white">
                        {partenaire.nom || "Sans nom"}
                      </p>

                      {partenaire.link_url ? (
                        <a
                          href={partenaire.link_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 block line-clamp-1 text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                          {partenaire.link_url}
                        </a>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">
                          Aucun lien web
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Ordre {partenaire.ordre || "—"}
                        </span>

                        {partenaire.is_detail ? (
                          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                            Page détail
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-950/70">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
              <FaHandshake />
            </div>

            <p className="mt-4 text-sm font-bold text-slate-800 dark:text-slate-200">
              Aucun partenaire actif à afficher.
            </p>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Activez au moins un partenaire pour le voir apparaître dans
              l’aperçu public.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}